export type CheckoutPackagingInfo = {
  isBox: boolean
  packagingMode: 'unit' | 'box'
  packagingQuantity: number
  packagingLabel?: string
}

const roundCheckoutPackagingNumber = (value: number, decimals = 4) => {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

const resolveCheckoutPackagingText = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value !== 'string') continue
    const normalized = value.trim()
    if (normalized) return normalized
  }

  return ''
}

const resolveCheckoutPackagingBoolean = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'boolean') return value
    if (typeof value === 'number' && Number.isFinite(value)) return value === 1
    if (typeof value === 'string') {
      const normalized = value.trim().toLocaleLowerCase('pt-BR')
      if (!normalized) continue
      if (['1', 'true', 'sim', 'yes', 'on'].includes(normalized)) return true
      if (['0', 'false', 'nao', 'não', 'no', 'off'].includes(normalized)) return false
    }
  }

  return false
}

const resolveCheckoutPackagingNumber = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') {
      const normalized = value.trim().replace(/\./g, '').replace(',', '.')
      if (!normalized) continue
      const parsed = Number(normalized)
      if (Number.isFinite(parsed)) return parsed
    }
  }

  return 0
}

export const resolveCheckoutPackagingInfo = (item: Record<string, unknown>): CheckoutPackagingInfo => {
  const explicitPackagingMode = resolveCheckoutPackagingText(item, ['packaging_mode', 'packagingMode'])
    .toLocaleLowerCase('pt-BR')
  const explicitBoxFlag = resolveCheckoutPackagingBoolean(item, ['caixa', 'is_box', 'isBox'])
  const explicitPackagingQuantity = Math.max(
    1,
    resolveCheckoutPackagingNumber(item, ['qtde_caixa', 'qtdeCaixa', 'packaging_quantity', 'packagingQuantity']) || 1
  )

  const packagingLabel = (
    resolveCheckoutPackagingText(item, ['embalagem', 'packaging_label', 'packagingLabel'])
    || (
      explicitBoxFlag
      || explicitPackagingMode === 'box'
      || explicitPackagingQuantity > 1
        ? `CAIXA C/ ${Math.trunc(explicitPackagingQuantity)}`
        : 'UNITARIO'
    )
  )

  if (explicitBoxFlag || explicitPackagingMode === 'box' || explicitPackagingQuantity > 1) {
    return {
      isBox: true,
      packagingMode: 'box',
      packagingQuantity: Math.max(1, Math.trunc(explicitPackagingQuantity)),
      packagingLabel,
    }
  }

  return {
    isBox: false,
    packagingMode: 'unit',
    packagingQuantity: 1,
    packagingLabel,
  }
}

export const resolveCheckoutPackagingInfoFromRecords = (
  records: Array<Record<string, unknown> | null | undefined>
): CheckoutPackagingInfo => {
  let fallback: CheckoutPackagingInfo = {
    isBox: false,
    packagingMode: 'unit',
    packagingQuantity: 1,
    packagingLabel: 'UNITARIO',
  }

  for (const record of records) {
    if (!record) continue
    const packaging = resolveCheckoutPackagingInfo(record)
    if (packaging.isBox) return packaging
    fallback = packaging
  }

  return fallback
}

export const resolveCheckoutDisplayQuantity = (
  quantity: number,
  packaging: Pick<CheckoutPackagingInfo, 'isBox' | 'packagingQuantity'>
) => {
  if (!(quantity > 0)) return quantity
  if (!packaging.isBox) return quantity
  return roundCheckoutPackagingNumber(quantity * Math.max(1, packaging.packagingQuantity), 4)
}

export const resolveCheckoutDisplayUnitPrice = (
  unitPrice: number,
  packaging: Pick<CheckoutPackagingInfo, 'isBox' | 'packagingQuantity'>
) => {
  if (!(unitPrice > 0)) return unitPrice
  if (!packaging.isBox) return unitPrice
  return roundCheckoutPackagingNumber(unitPrice / Math.max(1, packaging.packagingQuantity), 9)
}
